/**
 * PAKETPOSITION MIT BESTANDTEILEN — der Netzdienst.
 *
 * Lesen der Eintraege, Aenderungen, Ausgaben, des Stundenprotokolls und des
 * FRISCHEN Positionsstands; die drei RPCs aus SQL Nr. 166
 * (`bestandteil_erledigen`, `bestandteil_entfaellt`,
 * `bestandteil_zuruecknehmen` — die ersten beiden seit Nr. 172 mit
 * `p_stunden`) und die zwei Stunden-RPCs aus Nr. 172
 * (`bestandteil_stunden_setzen`, `position_stundenkontingent_setzen`).
 * Regeln und Typen stehen in bestandteilLogic.ts und stundenLogic.ts; was nach
 * einer Erledigung mit der POSITION geschieht (automatische Buchung), in
 * bestandteilBuchung.ts.
 *
 * ─── Lesen: seitenweise ────────────────────────────────────────────────────
 * PostgREST kappt eine Antwort ohne Bereichsangabe still bei 1000 Zeilen —
 * ohne Fehler. Eine gekappte Eintragsliste hiesse hier „Bestandteil offen",
 * obwohl er erledigt ist, und der Dialog boete ein zweites Erledigen an.
 * Deshalb blaettert jeder Leseweg (`range`), bis eine Seite nicht mehr voll
 * ist.
 *
 * ─── Lesen: nie werfen, aber nie still ─────────────────────────────────────
 * Ergebnis ist `{ ok: true, … }` oder `{ ok: false, fehler }`. Wer den Fehler
 * ignoriert, haette „keine Eintraege" gelesen — das ist bei Bestandteilen
 * die gefaehrliche Richtung (alles offen, Übernahme-Vorschlaege fuer laengst
 * Erledigtes). Der Aufrufer zeigt den Fehler an.
 *
 * ─── RPCs: fachliche Ergebnisse sind Status, keine Fehler ─────────────────
 * Die Datenbank antwortet mit `{status, eintrag_id, offen_danach}`. Rechte
 * (nicht angemeldet, Freelancer, kein Projektzugriff, nicht Admin) kommen als
 * SQLSTATE 42501 — hier zu `kein_zugriff` bzw. `nur_admin`. Fehlt die
 * Funktion (Nr. 166 nicht gefahren: PGRST202, 42883), heisst der Status
 * `rpc_fehlt`. Alles andere ist `fehler` mit Text. Die Funktionen werfen nie.
 *
 * ─── Stunden (Nr. 171/172): Rueckfall statt Ladefehler ────────────────────
 * Die Stunden sind rein intern (S8). Fehlt ihre Spalte oder das Protokoll in
 * dieser Datenbank, laden Dialog und Paketpositionen trotzdem — ohne Stunden
 * (`stundenVerfuegbar: false`) bzw. mit leerem Protokoll. Ausgeloest wird der
 * Rueckfall nur von einem Fehler, der genau EINE der Stundenspalten nennt
 * (bzw. die fehlende Protokolltabelle) — nie geraten.
 *
 * KEIN Zugriff auf die Projekttabelle hier (Abgrenzung Uebungsprojekte,
 * uebung/abgrenzung.test.ts) — gelesen wird nur `project_services` und die
 * Tabellen aus Nr. 163/165/171.
 */

import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { bestandteilFelderAusZeile } from '../projectLv';
import { spalteAusMeldung } from '../spaltenRueckfall';
import { isBuildGuardWriteError } from '../versionGate';
import { isMaintenanceWriteError } from '../maintenanceLogic';
import type { PositionsBestandteil } from '../../types';
import {
    aenderungAusZeile,
    eintragAusZeile,
    hatBestandteile,
    type AusgabeLike,
    type BestandteilAenderung,
    type BestandteilEintrag,
} from './bestandteilLogic';
import { stundenAusDb, stundenProtokollAusZeile, type StundenProtokollEintrag } from './stundenLogic';

// ─────────────────────────────────────────────────────────────────────────────
// Status der RPCs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Der Ausgang eines RPC-Aufrufs — die Status-Werte von SQL Nr. 166 WORTGLEICH
 * (`entfaellt`, `grund_zu_kurz`, `eintrag_fehlt`), dazu die Client-Status fuer
 * Fehler, die die Datenbank nicht als Status, sondern als Fehler meldet:
 *
 *  - `kein_zugriff` / `nur_admin` — SQLSTATE 42501 (nicht angemeldet,
 *    Freelancer, fremdes Projekt / nicht Admin bei `entfaellt`, fremder
 *    Eintrag bei der Ruecknahme);
 *  - `build_veraltet` — der Build-Riegel (Signalwort BUILD_VERALTET): dieses
 *    Bundle darf nicht mehr schreiben, App neu laden;
 *  - `bauphase` — Wartung (Signalwort BAUPHASE), spaeter erneut versuchen;
 *  - `veraltet` — SQLSTATE 40001 aus `bestandteil_zuruecknehmen`: Eintrag und
 *    Position passen nicht mehr zusammen (gleichzeitige Schluesselaenderung,
 *    Position umgehaengt) — neu laden;
 *  - `rpc_fehlt` — Nr. 166 nicht gefahren (PGRST202/42883) — oder, mit
 *    Stunden gerufen, Nr. 172 nicht (die Fassung mit `p_stunden` fehlt);
 *  - `fehler` — alles andere, mit Text.
 *
 * `stunden_ungueltig` (Nr. 172): die Stunden verletzen S9 — nichts
 * geschrieben. Der Client meldet ihn auch selbst, ohne Netz, wenn er keine
 * Zahl bekommt (NaN/Infinity — sonst kaeme sie als JSON-null an und der
 * Bestandteil waere ohne Stunden erledigt).
 */
export type BestandteilRpcStatus =
    | 'erledigt'
    | 'schon_erledigt'
    | 'entfaellt'
    | 'zurueckgenommen'
    | 'schon_zurueckgenommen'
    | 'unbekannter_bestandteil'
    | 'position_fehlt'
    | 'eintrag_fehlt'
    | 'leistungstag_zukunft'
    | 'ausgabe_fremd'
    | 'grund_zu_kurz'
    | 'stunden_ungueltig'
    | 'nur_admin'
    | 'abgerechnet'
    | 'abrechnungsstand_unbekannt'
    | 'veraltet'
    | 'kein_zugriff'
    | 'build_veraltet'
    | 'bauphase'
    | 'rpc_fehlt'
    | 'fehler';

/**
 * Die Status, die der CLIENT aus einem Fehler macht (nicht aus einer
 * Antwort) — gleich fuer alle RPCs dieser Datei, deshalb in jedem
 * Ergebnistyp enthalten.
 */
type ClientFehlerStatus = 'nur_admin' | 'kein_zugriff' | 'build_veraltet' | 'bauphase' | 'veraltet' | 'rpc_fehlt' | 'fehler';

export interface BestandteilRpcErgebnis {
    status: BestandteilRpcStatus;
    eintragId?: string;
    /** Offene Bestandteile der Position NACH dem Aufruf (DB-Wert); null = unbekannt. */
    offenDanach?: number | null;
    /** Nur bei der Ruecknahme: zu welcher Position der Eintrag gehoert (die DB nennt es mit). */
    projectId?: string;
    serviceId?: string;
    /** Klartext bei `fehler` (und bei Rechtefehlern die Meldung der Datenbank). */
    fehler?: string;
}

/** Deutsche Meldung je Status — fuer Dialog und Abschlussschritt. */
export const RPC_STATUS_TEXT: Record<BestandteilRpcStatus, string> = {
    erledigt: 'Bestandteil erledigt.',
    schon_erledigt: 'Dieser Bestandteil ist bereits erledigt (oder entfällt) — es wurde nichts doppelt gebucht.',
    entfaellt: 'Bestandteil entfällt.',
    zurueckgenommen: 'Eintrag zurückgenommen — der Bestandteil ist wieder offen.',
    schon_zurueckgenommen: 'Dieser Eintrag war bereits zurückgenommen.',
    unbekannter_bestandteil: 'Diesen Bestandteil gibt es in der Position nicht (mehr). Bitte neu laden.',
    position_fehlt: 'Die Position wurde im Projekt nicht gefunden. Bitte neu laden.',
    eintrag_fehlt: 'Der Eintrag wurde nicht gefunden. Bitte neu laden.',
    leistungstag_zukunft: 'Der Leistungstag darf nicht in der Zukunft liegen.',
    ausgabe_fremd: 'Diese Ausgabe gehört nicht zu diesem Projekt oder passt nicht zu diesem Bestandteil.',
    grund_zu_kurz: 'Bitte einen Grund mit mindestens 5 Zeichen angeben.',
    stunden_ungueltig: 'Ungültige Stundenzahl — erlaubt sind mehr als 0 bis 9999 h (bei „entfällt“ auch 0), höchstens zwei Nachkommastellen. Es wurde nichts gespeichert.',
    nur_admin: 'Das darf nur ein Admin (zurücknehmen: der eigene Eintrag oder ein Admin).',
    abgerechnet: 'Die Position ist bereits abgerechnet. Erst stornieren, dann den Bestandteil zurücknehmen.',
    abrechnungsstand_unbekannt: 'Abrechnungsstand der Position unbekannt — Rücknahme gesperrt, bitte Buchhaltung.',
    veraltet: 'Eintrag und Position passen nicht mehr zusammen — bitte neu laden.',
    kein_zugriff: 'Keine Berechtigung für dieses Projekt.',
    build_veraltet: 'Es gibt eine neue Programmversion. Bitte die App neu laden und den Vorgang danach wiederholen — es wurde nichts gespeichert.',
    bauphase: 'Die App wird gerade aktualisiert (Bauphase). Bitte nach dem Ende der Bauphase erneut versuchen — es wurde nichts gespeichert.',
    rpc_fehlt: 'Die Bestandteile sind in dieser Umgebung noch nicht vollständig eingerichtet (Migration Nr. 166 bzw. 172 fehlt). Bitte an die Verwaltung wenden.',
    fehler: 'Der Vorgang ist fehlgeschlagen. Bitte erneut versuchen.',
};

/**
 * Status der Datenbank → Status des Clients (wortgleich). Gelesen werden
 * zusaetzlich die Schreibweisen des ersten Vertragsentwurfs (`entfallen`,
 * `grund_fehlt`) — ein Namensunterschied zwischen getrennt entstandenem
 * Client und SQL darf aus einem gelungenen Vorgang keinen „Fehler" machen.
 */
const DB_STATUS: Record<string, BestandteilRpcStatus> = {
    erledigt: 'erledigt',
    schon_erledigt: 'schon_erledigt',
    entfaellt: 'entfaellt',
    entfallen: 'entfaellt',
    zurueckgenommen: 'zurueckgenommen',
    schon_zurueckgenommen: 'schon_zurueckgenommen',
    unbekannter_bestandteil: 'unbekannter_bestandteil',
    position_fehlt: 'position_fehlt',
    eintrag_fehlt: 'eintrag_fehlt',
    leistungstag_zukunft: 'leistungstag_zukunft',
    ausgabe_fremd: 'ausgabe_fremd',
    grund_zu_kurz: 'grund_zu_kurz',
    grund_fehlt: 'grund_zu_kurz',
    stunden_ungueltig: 'stunden_ungueltig',
    nur_admin: 'nur_admin',
    abgerechnet: 'abgerechnet',
    abrechnungsstand_unbekannt: 'abrechnungsstand_unbekannt',
};

/**
 * Status-Lookup nur ueber EIGENE Schluessel — `map['constructor']` waere
 * sonst die Funktion von Object.prototype und ginge als „Status" durch.
 */
const statusAus = <S extends string>(map: Record<string, S>, roh: string): S | undefined =>
    roh && Object.prototype.hasOwnProperty.call(map, roh) ? map[roh] : undefined;

const fehlerCode = (e: unknown): string => {
    const c = (e as { code?: unknown } | null)?.code;
    return typeof c === 'string' ? c : '';
};

const fehlerMeldung = (e: unknown): string => String((e as { message?: unknown } | null)?.message ?? '');

/** Fehlt die Funktion in der Datenbank? (PostgREST bzw. Postgres) */
const istFehlendeFunktion = (e: unknown): boolean =>
    fehlerCode(e) === 'PGRST202' || fehlerCode(e) === '42883' || /could not find the function/i.test(fehlerMeldung(e));

/** Fehlt die Tabelle (Nr. 163/165 nicht gefahren)? */
const istFehlendeTabelle = (e: unknown): boolean => fehlerCode(e) === 'PGRST205' || fehlerCode(e) === '42P01';

/**
 * Fehlt eine Spalte (Nr. 165/171 nicht gefahren)? 42703 kommt aus Postgres
 * (Lesen), PGRST204 aus dem Schema-Cache von PostgREST (Schreibweg) — hier
 * wird nur gelesen, der zweite Code steht der Vollstaendigkeit halber da.
 */
const istFehlendeSpalte = (e: unknown): boolean => fehlerCode(e) === '42703' || fehlerCode(e) === 'PGRST204';

/**
 * Fehlt GENAU eine dieser Stundenspalten (Nr. 171)? Nur dann gibt es den
 * Rueckfall ohne Stunden. Die Spalte muss in der Meldung stehen
 * (spaltenRueckfall.spalteAusMeldung) — nennt sie eine andere Spalte oder gar
 * keine, bleibt es beim Ladefehler wie bisher: geraten wird nicht.
 */
const fehltStundenSpalte = (e: unknown, spalten: readonly string[]): boolean => {
    if (!istFehlendeSpalte(e)) return false;
    const bezug = spalteAusMeldung(fehlerMeldung(e));
    return !!bezug && spalten.includes(bezug.spalte);
};

/**
 * Rechtefehler der RPC (42501) → `nur_admin` oder `kein_zugriff`.
 *
 * Die RPCs werfen beides mit demselben Code; unterschieden wird an der
 * Meldung („nur ein Admin", „nur der eigene Eintrag oder ein Admin", bei den
 * Stunden „… erfasst hat, oder ein Admin"). Faellt der Text anders aus, bleibt
 * es bei `kein_zugriff` — die vorsichtigere Auskunft.
 */
const rechteStatus = (e: unknown): 'nur_admin' | 'kein_zugriff' =>
    /nur ein admin|nur der eigene eintrag|oder ein admin/i.test(fehlerMeldung(e)) ? 'nur_admin' : 'kein_zugriff';

const textOder = (v: unknown): string | undefined => {
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    return undefined;
};

const offenAus = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Unbekannter Status / keine Antwort — fuer alle RPCs derselbe Text. */
const unerwartet = (roh: string): { status: 'fehler'; fehler: string } => ({
    status: 'fehler',
    fehler: `Unerwartete Antwort der Datenbank${roh ? ` (Status „${roh}“)` : ''}.`,
});

/**
 * Die jsonb-Antwort einer RPC lesen. Unbekannter Status → `fehler` mit dem
 * Rohwert im Text: lieber eine sichtbare Meldung als eine geratene Deutung.
 */
export const leseRpcAntwort = (data: unknown): BestandteilRpcErgebnis => {
    const o = data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : null;
    const roh = typeof o?.status === 'string' ? o.status.trim() : '';
    const status = statusAus(DB_STATUS, roh);
    if (!o || !status) return unerwartet(roh);
    const eintragId = textOder(o.eintrag_id);
    const projectId = textOder(o.project_id);
    const serviceId = textOder(o.service_id);
    return {
        status,
        ...(eintragId ? { eintragId } : {}),
        offenDanach: offenAus(o.offen_danach),
        ...(projectId ? { projectId } : {}),
        ...(serviceId ? { serviceId } : {}),
    };
};

/**
 * Fehler eines RPC-Aufrufs → Ergebnis. Die beiden Riegel zuerst: ihr
 * Signalwort ist die ausdrueckliche Auskunft des Servers, der Code daneben
 * (400, P0001, je nach Weg auch 42501) waere nur geraten.
 */
const ausFehler = (e: unknown): { status: ClientFehlerStatus; fehler: string } => {
    if (isBuildGuardWriteError(e)) return { status: 'build_veraltet', fehler: describeError(e) };
    if (isMaintenanceWriteError(e)) return { status: 'bauphase', fehler: describeError(e) };
    if (fehlerCode(e) === '40001') return { status: 'veraltet', fehler: describeError(e) };
    if (istFehlendeFunktion(e)) return { status: 'rpc_fehlt', fehler: describeError(e) };
    if (fehlerCode(e) === '42501') return { status: rechteStatus(e), fehler: describeError(e) };
    return { status: 'fehler', fehler: describeError(e) };
};

/** Ein RPC-Aufruf, der nie wirft: Antwort ueber `lesen`, jeder Fehler ueber `ausFehler`. */
const rpcAufruf = async <E>(name: string, args: Record<string, unknown>, lesen: (data: unknown) => E): Promise<E | ReturnType<typeof ausFehler>> => {
    try {
        const { data, error } = await supabase.rpc(name, args);
        if (error) return ausFehler(error);
        return lesen(data);
    } catch (e) {
        // Manche Clients werfen statt abzulehnen — dieselben Unterscheidungen.
        return ausFehler(e);
    }
};

const rufeRpc = (name: string, args: Record<string, unknown>): Promise<BestandteilRpcErgebnis> =>
    rpcAufruf(name, args, leseRpcAntwort);

/**
 * `p_stunden` fuer erledigen/entfaellt: nur wenn gesetzt (sonst bleibt der
 * Aufruf wortgleich zu Nr. 166 und laeuft auch gegen eine Datenbank ohne
 * Nr. 172). `undefined`/`null` → nicht gesetzt; jede andere Nicht-Zahl →
 * ungueltig (JSON machte aus NaN still `null` = „ohne Stunden erledigt").
 */
const stundenArg = (stunden: unknown): { ok: true; args: Record<string, unknown> } | { ok: false } => {
    if (stunden === undefined || stunden === null) return { ok: true, args: {} };
    if (typeof stunden !== 'number' || !Number.isFinite(stunden)) return { ok: false };
    return { ok: true, args: { p_stunden: stunden } };
};

/** Grund: getrimmt, leer → null (die Datenbank prueft die Laenge). */
const grundArg = (grund: string | null | undefined): string | null =>
    typeof grund === 'string' && grund.trim() ? grund.trim() : null;

// ─────────────────────────────────────────────────────────────────────────────
// RPCs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Einen Bestandteil von Hand erledigen (oder eine Ausgabe uebernehmen, mit
 * `ausgabeId`). Zeitpunkt und Person setzt die Datenbank; `leistungstag`
 * fehlt → heute (Berlin) bzw. der Tag der Ausgabe.
 *
 * `stunden` (S2, Pflicht im Dialog): geht als `p_stunden` nur mit, wenn
 * gesetzt; den Wertebereich (0 < h ≤ 9999, zwei Nachkommastellen) prueft die
 * Datenbank (`stunden_ungueltig`).
 *
 * Die POSITION bucht diese Funktion nicht — ist `offenDanach === 0`, ruft der
 * Dialog danach `nachHandErledigung` (bestandteilBuchung.ts).
 */
export const erledigeBestandteil = async (a: {
    projectId: string;
    serviceId: string;
    key: string;
    leistungstag?: string;
    notiz?: string;
    ausgabeId?: string;
    stunden?: number;
}): Promise<BestandteilRpcErgebnis> => {
    const stunden = stundenArg(a.stunden);
    if (stunden.ok === false) return { status: 'stunden_ungueltig', offenDanach: null };
    return rufeRpc('bestandteil_erledigen', {
        p_project_id: a.projectId,
        p_service_id: a.serviceId,
        p_key: a.key,
        p_leistungstag: a.leistungstag || null,
        p_notiz: a.notiz?.trim() ? a.notiz.trim() : null,
        p_ausgabe_id: a.ausgabeId || null,
        ...stunden.args,
    });
};

/**
 * Nur Admin (E4): ein Bestandteil entfaellt, Grund Pflicht (>= 5 Zeichen,
 * prueft die DB). `stunden` freiwillig (S2), 0 erlaubt — `p_stunden` nur wenn
 * gesetzt.
 */
export const bestandteilEntfaellt = async (a: {
    projectId: string;
    serviceId: string;
    key: string;
    grund: string;
    stunden?: number;
}): Promise<BestandteilRpcErgebnis> => {
    const stunden = stundenArg(a.stunden);
    if (stunden.ok === false) return { status: 'stunden_ungueltig', offenDanach: null };
    return rufeRpc('bestandteil_entfaellt', {
        p_project_id: a.projectId,
        p_service_id: a.serviceId,
        p_key: a.key,
        p_grund: a.grund,
        ...stunden.args,
    });
};

/**
 * Einen Eintrag zuruecknehmen (eigener Eintrag oder Admin, Grund Pflicht;
 * gesperrt, wenn die Position abgerechnet ist). Die automatische
 * Positionsbuchung nimmt danach `nachBestandteilRuecknahme` zurueck.
 */
export const nimmBestandteilZurueck = (a: { eintragId: string; grund: string }): Promise<BestandteilRpcErgebnis> =>
    rufeRpc('bestandteil_zuruecknehmen', {
        p_eintrag_id: a.eintragId,
        p_grund: a.grund,
    });

// ─────────────────────────────────────────────────────────────────────────────
// Stunden-RPCs (SQL Nr. 172)
// ─────────────────────────────────────────────────────────────────────────────

/** Liest eine Stundenzahl aus der Antwort — nur wenn der Schluessel DA ist (sonst bleibt das Feld weg). */
const stundenFeld = <K extends string>(o: Record<string, unknown>, schluessel: string, feld: K): Partial<Record<K, number | null>> =>
    Object.prototype.hasOwnProperty.call(o, schluessel) ? ({ [feld]: stundenAusDb(o[schluessel]) } as Partial<Record<K, number | null>>) : {};

/**
 * `bestandteil_stunden_setzen` — Status der Datenbank WORTGLEICH (Nr. 172),
 * dazu die Client-Status aus Fehlern (siehe BestandteilRpcStatus):
 *  - `nur_admin` — die Stunden stehen schon, und die Person hat den Eintrag
 *    weder erfasst noch ist sie Admin (S7, 42501);
 *  - `veraltet` — Eintrag und Position passten beim Sperren nicht mehr
 *    zusammen (40001, wie bei der Ruecknahme).
 */
export type StundenRpcStatus =
    | 'gesetzt'
    | 'unveraendert'
    | 'eintrag_fehlt'
    | 'position_fehlt'
    | 'zurueckgenommen'
    | 'grund_zu_kurz'
    | 'stunden_ungueltig'
    | ClientFehlerStatus;

export interface StundenRpcErgebnis {
    status: StundenRpcStatus;
    eintragId?: string;
    projectId?: string;
    serviceId?: string;
    /** Stunden des Eintrags NACH dem Aufruf — nur, wenn die Datenbank sie nennt. */
    stunden?: number | null;
    /** Summe der Position NACH dem Aufruf (`project_services.stunden_erfasst`) — nur, wenn genannt. */
    stundenErfasst?: number | null;
    /** Kontingent der Position (unveraendert) — nur, wenn genannt. */
    stundenKontingent?: number | null;
    /** Klartext bei `fehler` (und bei Rechtefehlern die Meldung der Datenbank). */
    fehler?: string;
}

export const STUNDEN_RPC_STATUS_TEXT: Record<StundenRpcStatus, string> = {
    gesetzt: 'Stunden gespeichert.',
    unveraendert: 'Die Stunden waren schon so eingetragen — es wurde nichts geändert.',
    eintrag_fehlt: 'Der Eintrag wurde nicht gefunden. Bitte neu laden.',
    position_fehlt: 'Die Position wurde im Projekt nicht gefunden. Bitte neu laden.',
    zurueckgenommen: 'Dieser Eintrag ist zurückgenommen — seine Stunden lassen sich nicht mehr ändern.',
    grund_zu_kurz: 'Bitte einen Grund mit mindestens 5 Zeichen angeben.',
    stunden_ungueltig: 'Ungültige Stundenzahl — erlaubt sind mehr als 0 bis 9999 h (bei „entfällt“ auch 0), höchstens zwei Nachkommastellen. Es wurde nichts gespeichert.',
    nur_admin: 'Eingetragene Stunden ändert nur, wer den Eintrag erfasst hat, oder ein Admin.',
    kein_zugriff: 'Keine Berechtigung für dieses Projekt.',
    build_veraltet: RPC_STATUS_TEXT.build_veraltet,
    bauphase: RPC_STATUS_TEXT.bauphase,
    veraltet: 'Eintrag und Position passen nicht mehr zusammen — bitte neu laden.',
    rpc_fehlt: 'Die Stundenerfassung ist in dieser Umgebung noch nicht eingerichtet (Migration Nr. 172 fehlt). Bitte an die Verwaltung wenden.',
    fehler: 'Die Stunden konnten nicht gespeichert werden. Bitte erneut versuchen.',
};

const STUNDEN_DB_STATUS: Record<string, StundenRpcStatus> = {
    gesetzt: 'gesetzt',
    unveraendert: 'unveraendert',
    eintrag_fehlt: 'eintrag_fehlt',
    position_fehlt: 'position_fehlt',
    zurueckgenommen: 'zurueckgenommen',
    grund_zu_kurz: 'grund_zu_kurz',
    grund_fehlt: 'grund_zu_kurz',
    stunden_ungueltig: 'stunden_ungueltig',
};

/** Die jsonb-Antwort von `bestandteil_stunden_setzen`. Unbekannter Status → `fehler` mit Rohwert. */
export const leseStundenRpcAntwort = (data: unknown): StundenRpcErgebnis => {
    const o = data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : null;
    const roh = typeof o?.status === 'string' ? o.status.trim() : '';
    const status = statusAus(STUNDEN_DB_STATUS, roh);
    if (!o || !status) return unerwartet(roh);
    const eintragId = textOder(o.eintrag_id);
    const projectId = textOder(o.project_id);
    const serviceId = textOder(o.service_id);
    return {
        status,
        ...(eintragId ? { eintragId } : {}),
        ...(projectId ? { projectId } : {}),
        ...(serviceId ? { serviceId } : {}),
        ...stundenFeld(o, 'stunden', 'stunden'),
        ...stundenFeld(o, 'stunden_erfasst', 'stundenErfasst'),
        ...stundenFeld(o, 'stunden_kontingent', 'stundenKontingent'),
    };
};

/**
 * Stunden eines Eintrags nachtragen oder aendern (S7): fehlen sie, darf jeder
 * mit Projektzugriff; stehen sie, nur wer den Eintrag erfasst hat, oder ein
 * Admin — dann mit Grund (≥ 5 Zeichen). Rechte, Wertebereich und Grund prueft
 * die Datenbank; die Aenderung steht danach im Stundenprotokoll.
 */
export const setzeBestandteilStunden = async (a: {
    eintragId: string;
    stunden: number;
    grund?: string;
}): Promise<StundenRpcErgebnis> => {
    if (typeof a?.stunden !== 'number' || !Number.isFinite(a.stunden)) return { status: 'stunden_ungueltig' };
    return rpcAufruf('bestandteil_stunden_setzen', {
        p_eintrag_id: a.eintragId,
        p_stunden: a.stunden,
        p_grund: grundArg(a.grund),
    }, leseStundenRpcAntwort);
};

/**
 * `position_stundenkontingent_setzen` — Status WORTGLEICH (Nr. 172) plus die
 * Client-Status aus Fehlern. `nur_admin` heisst hier: die Person ist kein
 * Admin (42501).
 */
export type KontingentRpcStatus =
    | 'gesetzt'
    | 'unveraendert'
    | 'position_fehlt'
    | 'kein_paket'
    | 'stunden_ungueltig'
    | ClientFehlerStatus;

export interface KontingentRpcErgebnis {
    status: KontingentRpcStatus;
    /** Kontingent NACH dem Aufruf (null = keins) — nur, wenn die Datenbank es nennt. */
    stundenKontingent?: number | null;
    /** Summe der Position NACH dem Aufruf — nur, wenn genannt. */
    stundenErfasst?: number | null;
    fehler?: string;
}

export const KONTINGENT_RPC_STATUS_TEXT: Record<KontingentRpcStatus, string> = {
    gesetzt: 'Stundenkontingent gespeichert.',
    unveraendert: 'Das Stundenkontingent war schon so hinterlegt — es wurde nichts geändert.',
    position_fehlt: 'Die Position wurde im Projekt nicht gefunden. Bitte neu laden.',
    kein_paket: 'Ein Stundenkontingent gibt es nur bei Positionen mit Bestandteilen.',
    stunden_ungueltig: 'Ungültiges Kontingent — erlaubt sind mehr als 0 bis 99.999 h, höchstens zwei Nachkommastellen. Es wurde nichts gespeichert.',
    nur_admin: 'Das Stundenkontingent ändert nur ein Admin.',
    kein_zugriff: 'Keine Berechtigung für dieses Projekt.',
    build_veraltet: RPC_STATUS_TEXT.build_veraltet,
    bauphase: RPC_STATUS_TEXT.bauphase,
    veraltet: 'Die Position wurde inzwischen geändert — bitte neu laden.',
    rpc_fehlt: 'Das Stundenkontingent ist in dieser Umgebung noch nicht eingerichtet (Migration Nr. 172 fehlt). Bitte an die Verwaltung wenden.',
    fehler: 'Das Stundenkontingent konnte nicht gespeichert werden. Bitte erneut versuchen.',
};

const KONTINGENT_DB_STATUS: Record<string, KontingentRpcStatus> = {
    gesetzt: 'gesetzt',
    unveraendert: 'unveraendert',
    position_fehlt: 'position_fehlt',
    kein_paket: 'kein_paket',
    stunden_ungueltig: 'stunden_ungueltig',
};

/** Die jsonb-Antwort von `position_stundenkontingent_setzen`. Unbekannter Status → `fehler` mit Rohwert. */
export const leseKontingentRpcAntwort = (data: unknown): KontingentRpcErgebnis => {
    const o = data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : null;
    const roh = typeof o?.status === 'string' ? o.status.trim() : '';
    const status = statusAus(KONTINGENT_DB_STATUS, roh);
    if (!o || !status) return unerwartet(roh);
    return {
        status,
        ...stundenFeld(o, 'stunden_kontingent', 'stundenKontingent'),
        ...stundenFeld(o, 'stunden_erfasst', 'stundenErfasst'),
    };
};

/**
 * Das Stundenkontingent einer Paketposition festlegen, aendern oder (mit
 * `stunden: null`) entfernen — NUR Admin, auch nachtraeglich und nach
 * Abrechnung (S1, S8). Der einzige Weg, ein bestehendes Kontingent zu
 * aendern: beim Speichern des Projekts haelt der Trigger den alten Wert (S10).
 * Nach `gesetzt` die Position mit `stundenKontingent` UND `stundenErfasst`
 * aus der Antwort aktualisieren.
 */
export const setzeStundenKontingent = async (a: {
    projectId: string;
    serviceId: string;
    stunden: number | null;
    grund?: string;
}): Promise<KontingentRpcErgebnis> => {
    const stunden = a?.stunden ?? null;
    if (stunden !== null && (typeof stunden !== 'number' || !Number.isFinite(stunden))) return { status: 'stunden_ungueltig' };
    return rpcAufruf('position_stundenkontingent_setzen', {
        p_project_id: a.projectId,
        p_service_id: a.serviceId,
        p_stunden: stunden,
        p_grund: grundArg(a.grund),
    }, leseKontingentRpcAntwort);
};

// ─────────────────────────────────────────────────────────────────────────────
// Lesen
// ─────────────────────────────────────────────────────────────────────────────

/** Seitengroesse — genau die Kappungsgrenze von PostgREST. */
const SEITE = 1000;

type Abfrage = (von: number, bis: number) => PromiseLike<{ data: unknown; error: unknown }>;

/** Alle Seiten einer Abfrage; wirft beim ersten Fehler. */
const alleSeiten = async (abfrage: Abfrage): Promise<Record<string, unknown>[]> => {
    const out: Record<string, unknown>[] = [];
    for (let seite = 0; ; seite += 1) {
        const von = seite * SEITE;
        const { data, error } = await abfrage(von, von + SEITE - 1);
        if (error) throw error;
        const zeilen = Array.isArray(data) ? (data as unknown[]) : [];
        for (const z of zeilen) {
            if (z && typeof z === 'object' && !Array.isArray(z)) out.push(z as Record<string, unknown>);
        }
        if (zeilen.length < SEITE) return out;
    }
};

const leseFehler = (e: unknown, was: string): string => {
    if (istFehlendeTabelle(e) || istFehlendeSpalte(e)) {
        return `${was}: die Bestandteile sind in dieser Umgebung noch nicht eingerichtet (Migration Nr. 165/166 fehlt).`;
    }
    return `${was}: ${describeError(e)}`;
};

const ohneProjekt = (projectId: string): boolean => !projectId || projectId === 'temp-id';

/** Die Spalten aus Nr. 165 — zugleich der Rueckfall fuer eine Datenbank ohne Nr. 171. */
const EINTRAG_SPALTEN_165 =
    'id, project_id, service_id, bestandteil_key, art, leistungstag, durch, ausgabe_id, notiz, '
    + 'erfasst_am, erfasst_von, erfasst_von_name, '
    + 'zurueckgenommen_am, zurueckgenommen_von, zurueckgenommen_von_name, zurueckgenommen_grund';

const EINTRAG_SPALTEN = `${EINTRAG_SPALTEN_165}, stunden`;

const AENDERUNG_SPALTEN = 'id, project_id, service_id, vorher, nachher, geaendert_am, geaendert_von, geaendert_von_name';

const PROTOKOLL_SPALTEN =
    'id, project_id, service_id, eintrag_id, art, vorher, nachher, grund, geaendert_am, geaendert_von, geaendert_von_name';

/** Erfolgreiches Ergebnis von `ladeBestandteilEintraege`. */
export interface BestandteilDaten {
    ok: true;
    eintraege: BestandteilEintrag[];
    aenderungen: BestandteilAenderung[];
    /** Stunden- und Kontingentaenderungen (Nr. 171) — leer, wenn es die Tabelle hier noch nicht gibt. */
    stundenProtokoll: StundenProtokollEintrag[];
    /**
     * false: die Datenbank kennt die Stunden noch nicht (Spalte `stunden`
     * fehlt, Nr. 171 nicht gefahren). Dann sind die Stunden aller Eintraege
     * UNBEKANNT, nicht „fehlen" — der Dialog fragt keine nach.
     */
    stundenVerfuegbar: boolean;
}

/**
 * Alle Eintraege (auch zurueckgenommene — sie gehoeren in den Verlauf), alle
 * Aenderungen der Definition und das Stundenprotokoll eines Projekts.
 * Unbrauchbare Zeilen fallen heraus (eintragAusZeile,
 * stundenProtokollAusZeile), der Rest bleibt.
 *
 * Stunden (Nr. 171): fehlt die Spalte `stunden`, wird ohne sie gelesen
 * (`stundenVerfuegbar: false`); fehlt die Protokolltabelle, bleibt das
 * Protokoll leer. Beides ist nie ein Ladefehler des Dialogs — jeder ANDERE
 * Fehler bleibt es (nie still).
 */
export const ladeBestandteilEintraege = async (
    projectId: string,
): Promise<BestandteilDaten | { ok: false; fehler: string }> => {
    if (ohneProjekt(projectId)) return { ok: true, eintraege: [], aenderungen: [], stundenProtokoll: [], stundenVerfuegbar: true };
    let eintragZeilen: Record<string, unknown>[];
    let aenderungZeilen: Record<string, unknown>[];
    let protokollZeilen: Record<string, unknown>[];
    let stundenVerfuegbar = true;
    const leseEintraege = (spalten: string) =>
        alleSeiten((von, bis) =>
            supabase
                .from('position_bestandteil_eintraege')
                .select(spalten)
                .eq('project_id', projectId)
                .order('erfasst_am', { ascending: true })
                .order('id', { ascending: true })
                .range(von, bis),
        );
    try {
        try {
            eintragZeilen = await leseEintraege(EINTRAG_SPALTEN);
        } catch (e) {
            if (!fehltStundenSpalte(e, ['stunden'])) throw e;
            stundenVerfuegbar = false;
            eintragZeilen = await leseEintraege(EINTRAG_SPALTEN_165);
        }
    } catch (e) {
        return { ok: false, fehler: leseFehler(e, 'Bestandteile nicht lesbar') };
    }
    try {
        aenderungZeilen = await alleSeiten((von, bis) =>
            supabase
                .from('position_bestandteil_aenderungen')
                .select(AENDERUNG_SPALTEN)
                .eq('project_id', projectId)
                .order('geaendert_am', { ascending: true })
                .order('id', { ascending: true })
                .range(von, bis),
        );
    } catch (e) {
        return { ok: false, fehler: leseFehler(e, 'Änderungen der Bestandteile nicht lesbar') };
    }
    try {
        protokollZeilen = await alleSeiten((von, bis) =>
            supabase
                .from('position_stunden_protokoll')
                .select(PROTOKOLL_SPALTEN)
                .eq('project_id', projectId)
                .order('geaendert_am', { ascending: true })
                .order('id', { ascending: true })
                .range(von, bis),
        );
    } catch (e) {
        // Datenbank ohne Nr. 171: kein Protokoll, keine Stundenzeilen im Verlauf.
        if (!istFehlendeTabelle(e) && !istFehlendeSpalte(e)) {
            return { ok: false, fehler: `Stundenprotokoll nicht lesbar: ${describeError(e)}` };
        }
        protokollZeilen = [];
    }
    const eintraege = eintragZeilen.map(eintragAusZeile).filter((e): e is BestandteilEintrag => e !== null);
    const aenderungen = aenderungZeilen.map(aenderungAusZeile).filter((a): a is BestandteilAenderung => a !== null);
    const stundenProtokoll = protokollZeilen
        .map(stundenProtokollAusZeile)
        .filter((p): p is StundenProtokollEintrag => p !== null);
    return { ok: true, eintraege, aenderungen, stundenProtokoll, stundenVerfuegbar };
};

/**
 * Die Freigaben und Versendungen eines Projekts (`projekt_unterlagen_ausgaben`,
 * Nr. 163) fuer den Uebernahme-Vorschlag. Bewusst ohne `auszug` (jsonb,
 * gross) — der Vorschlag braucht nur Unterlage, Anlass, Zeitpunkt, Person.
 */
export const ladeAusgabenFuerUebernahme = async (
    projectId: string,
): Promise<{ ok: true; ausgaben: AusgabeLike[] } | { ok: false; fehler: string }> => {
    if (ohneProjekt(projectId)) return { ok: true, ausgaben: [] };
    try {
        const zeilen = await alleSeiten((von, bis) =>
            supabase
                .from('projekt_unterlagen_ausgaben')
                .select('id, unterlage, anlass, erstellt_am, erstellt_von_name, fassung_ref')
                .eq('project_id', projectId)
                .in('anlass', ['freigabe', 'versand'])
                .order('erstellt_am', { ascending: true })
                .order('id', { ascending: true })
                .range(von, bis),
        );
        const ausgaben: AusgabeLike[] = [];
        for (const z of zeilen) {
            const id = textOder(z.id);
            const unterlage = textOder(z.unterlage);
            const anlass = textOder(z.anlass);
            const erstelltAm = textOder(z.erstellt_am);
            if (!id || !unterlage || !anlass || !erstelltAm) continue;
            ausgaben.push({
                id,
                unterlage,
                anlass,
                erstelltAm,
                erstelltVonName: textOder(z.erstellt_von_name) ?? null,
                fassungRef: textOder(z.fassung_ref) ?? null,
            });
        }
        return { ok: true, ausgaben };
    } catch (e) {
        if (istFehlendeTabelle(e)) {
            return { ok: false, fehler: 'Ausgaben nicht lesbar: die Tabelle fehlt noch (Migration Nr. 163).' };
        }
        return { ok: false, fehler: `Ausgaben nicht lesbar: ${describeError(e)}` };
    }
};

/**
 * Eine Paketposition, wie sie JETZT in der Datenbank steht — Grundlage jeder
 * automatischen Positionsbuchung. `bestandteileOffen` ist der DB-Wert (Trigger
 * Nr. 166), nie ein im Client gerechneter: Kernregel 7 bucht nur, wenn die
 * Datenbank „0 offen" sagt.
 */
export interface PaketPositionFrisch {
    id: string;
    title: string;
    lvPosition: string;
    bestandteile: PositionsBestandteil[] | null;
    /** null = unlesbar/unbekannt (fail closed: nicht vollstaendig). */
    bestandteileOffen: number | null;
    quantityUsed: number;
    quantityContracted: number;
    quantityBilled: number;
    /**
     * `quantity_billed` war in der Datenbank NICHT NULL. Ist der Stand
     * unbekannt (NULL), steht oben 0 — eine Ruecknahme darf das nie als
     * „nicht abgerechnet" lesen (Nr. 166 sperrt sie dann mit
     * `abrechnungsstand_unbekannt`). Fehlt das Feld (fremder Erzeuger), gilt
     * es als bekannt.
     */
    quantityBilledBekannt?: boolean;
    /** Eventualposition (`is_optional`) — wird nie automatisch gebucht. */
    istNep?: boolean;
    /**
     * Internes Stundenkontingent (`stunden_kontingent`, Nr. 171) — null: keins
     * hinterlegt. Fehlt das Feld, kennt die Datenbank die Spalte nicht.
     */
    stundenKontingent?: number | null;
    /**
     * Summe der Stunden aller aktiven Eintraege (`stunden_erfasst`, DB-gepflegt,
     * Nr. 172) — null: unbekannt. Fehlt das Feld, kennt die Datenbank die
     * Spalte nicht.
     */
    stundenErfasst?: number | null;
}

const zahl = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** Die beiden Spalten aus Nr. 171 auf `project_services`. */
const POSITION_STUNDEN_SPALTEN = ['stunden_kontingent', 'stunden_erfasst'] as const;

/**
 * Die Paketpositionen eines Projekts, frisch aus `project_services` — nur
 * Positionen MIT Bestandteilen. Die Spalten werden ueber
 * `bestandteilFelderAusZeile` gelesen (die eine Lesart, projectLv.ts), damit
 * hier dasselbe Gate gilt wie in jedem anderen Ladeweg.
 *
 * Stunden (Nr. 171): fehlt eine der beiden Spalten, wird mit `*` wiederholt —
 * `*` kann 42703 nicht ausloesen (PostgREST liefert, was da ist), und die
 * Liste bleibt fuer den Spaltenwaechter statisch lesbar. Die Stundenfelder
 * stehen dann nicht in der Zeile und fehlen im Ergebnis (unbekannt).
 */
export const ladePaketPositionenFrisch = async (
    projectId: string,
): Promise<{ ok: true; positionen: PaketPositionFrisch[] } | { ok: false; fehler: string }> => {
    if (ohneProjekt(projectId)) return { ok: true, positionen: [] };
    try {
        let zeilen: Record<string, unknown>[];
        try {
            zeilen = await alleSeiten((von, bis) =>
                supabase
                    .from('project_services')
                    .select('id, title, lv_position, bestandteile, bestandteile_offen, quantity_used, quantity_contracted, quantity_billed, is_optional, stunden_kontingent, stunden_erfasst')
                    .eq('project_id', projectId)
                    .not('bestandteile', 'is', null)
                    .order('id', { ascending: true })
                    .range(von, bis),
            );
        } catch (e) {
            if (!fehltStundenSpalte(e, POSITION_STUNDEN_SPALTEN)) throw e;
            zeilen = await alleSeiten((von, bis) =>
                supabase
                    .from('project_services')
                    .select('*')
                    .eq('project_id', projectId)
                    .not('bestandteile', 'is', null)
                    .order('id', { ascending: true })
                    .range(von, bis),
            );
        }
        const positionen: PaketPositionFrisch[] = [];
        for (const z of zeilen) {
            const id = textOder(z.id);
            const felder = bestandteilFelderAusZeile(z);
            if (!id || !hatBestandteile(felder)) continue;
            positionen.push({
                id,
                title: textOder(z.title) ?? '',
                lvPosition: textOder(z.lv_position) ?? '',
                bestandteile: felder.bestandteile ?? null,
                bestandteileOffen: felder.bestandteileOffen ?? null,
                quantityUsed: zahl(z.quantity_used),
                quantityContracted: zahl(z.quantity_contracted),
                quantityBilled: zahl(z.quantity_billed),
                quantityBilledBekannt: z.quantity_billed !== null && z.quantity_billed !== undefined && z.quantity_billed !== '',
                istNep: z.is_optional === true,
                // Nur, wenn die Spalte in der Zeile steht — fehlt sie (Rueckfall `*`
                // auf einer Datenbank ohne Nr. 171), bleibt das Feld weg: unbekannt.
                ...(Object.prototype.hasOwnProperty.call(z, 'stunden_kontingent')
                    ? { stundenKontingent: stundenAusDb(z.stunden_kontingent) }
                    : {}),
                ...(Object.prototype.hasOwnProperty.call(z, 'stunden_erfasst')
                    ? { stundenErfasst: stundenAusDb(z.stunden_erfasst) }
                    : {}),
            });
        }
        return { ok: true, positionen };
    } catch (e) {
        return { ok: false, fehler: leseFehler(e, 'Paketpositionen nicht lesbar') };
    }
};
